/**
 * Derives all share-card values from a CRE Incident object.
 * Pure function — no side effects, safe to import anywhere.
 */

function publicShareUrl(incident) {
  if (!incident) return null;
  const token = incident.share_token;
  if (incident.share_enabled && token) {
    return `${window.location.origin}/share/${token}`;
  }
  return `${window.location.origin}/incidents/${incident.id}`;
}

function publicShareDisplay(incident) {
  const u = publicShareUrl(incident);
  if (!u) return "";
  return u.replace(/^https?:\/\//, "").replace(/\/+$/, "");
}

function scoreRag(score) {
  if (score == null) return "amber";
  if (score >= 7.5) return "green";
  if (score >= 6.0) return "amber";
  return "red";
}

// Map a 0..1 confidence to a 5-dot label band + percentage. Prefers the
// triage classification confidence (more user-meaningful for the brief)
// and falls back to consensus alignment when triage isn't available.
function deriveConfidence(incident) {
  const triageConf = incident?.triage?.likely_incident_type_confidence;
  const alignment = incident?.consensus?.alignment_score;
  const raw =
    typeof triageConf === "number"
      ? triageConf
      : (typeof alignment === "number" ? alignment : 0);
  const a = Math.max(0, Math.min(1, Number(raw) || 0));
  let band;
  if (a >= 0.85) band = { dots: 5, label: "Very High", rag: "green" };
  else if (a >= 0.7) band = { dots: 4, label: "High", rag: "green" };
  else if (a >= 0.55) band = { dots: 3, label: "Medium", rag: "amber" };
  else if (a >= 0.4) band = { dots: 2, label: "Low", rag: "amber" };
  else band = { dots: 1, label: "Very Low", rag: "red" };
  const percent = Math.round(a * 100);
  return {
    ...band,
    percent,
    // Pill text used in the share card — e.g. "High — 79%".
    label_with_percent: a > 0 ? `${band.label} — ${percent}%` : band.label,
  };
}

// -----------------------------
// Business-friendly Impact composer.
//
// Builds a concise 1-2 sentence user/business impact statement from the
// incident description + symptoms + affected system. Does NOT surface
// raw system names unless we genuinely have nothing else to anchor on.
// Uses hedged language ("may", "likely", "potential") to avoid
// overclaiming. Technical detail stays in the Likely RCA field.
// -----------------------------
const IMPACT_PATTERNS = [
  // Order / commerce
  { rx: /\b(checkout|order|cart|purchas)/i, txt: "may face failed checkout attempts and order rejections" },
  { rx: /\b(payment|charge|transaction|debit|refund|invoice)/i, txt: "may see payment failures, debited-but-unconfirmed transactions, and delayed receipts" },
  { rx: /\b(inventory|stock|sku|availability)/i, txt: "may see stale inventory availability leading to order rejection and support contacts" },
  { rx: /\b(login|auth|signin|signup|register|sso|mfa|token)/i, txt: "may be unable to sign in or complete authentication" },
  { rx: /\b(search|catalog|browse|listing)/i, txt: "may see incomplete or delayed search results affecting product discovery" },
  // Communications / telco
  { rx: /\b(sms|notification|email\s*delivery|push\s*notification)/i, txt: "may not receive expected notifications and alerts" },
  { rx: /\b(call|voice|telephony|inbound|outbound|sip|number\s*port)/i, txt: "may be unable to receive inbound calls or experience routing failures" },
  // Data / sync
  { rx: /\b(sync|replicat|pipeline|etl|ingest|warehous)/i, txt: "may see stale or missing data across downstream systems" },
  { rx: /\b(report|dashboard|analytic|metric)/i, txt: "may see delayed or inconsistent reporting and dashboards" },
  // Latency / availability fallbacks
  { rx: /\b(latency|slow|timeout|hang|degrad)/i, txt: "may experience degraded performance, timeouts, and abandoned sessions" },
  { rx: /\b(outage|down|unavailable|503|502|500)/i, txt: "may be unable to access the affected service" },
  { rx: /\b(error|5xx|4xx|failure|reject)/i, txt: "may see request failures and inconsistent results" },
];

function _impactClauseFromText(text) {
  if (!text) return "";
  for (const p of IMPACT_PATTERNS) {
    if (p.rx.test(text)) return p.txt;
  }
  return "";
}

function deriveImpact(incident) {
  const symptoms = (incident?.symptoms || [])
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean);
  const sys = (incident?.affected_system || "").trim();
  const title = (incident?.title || "").trim();
  const desc = (incident?.description || "").trim();

  // Probe title + description + symptoms for the strongest matching
  // user-impact clause (deterministic, no LLM call).
  const probe = `${title} ${desc} ${symptoms.join(" ")}`.trim();
  const clause = _impactClauseFromText(probe);

  if (clause) {
    return `Customers ${clause}, with potential support contacts and revenue impact.`;
  }
  // Graceful fallbacks — keep impact framing, never expose system names
  // as the primary subject.
  if (symptoms.length) {
    return `Customers may experience ${symptoms.slice(0, 2).join(" and ")}, with potential support contacts.`;
  }
  if (sys && title) {
    return `Customers using ${sys} may experience disruption likely related to: ${title}.`;
  }
  if (title) {
    return `Customers may experience disruption likely related to: ${title}.`;
  }
  return "Customer-facing impact is being assessed.";
}

function deriveLikelyRCA(incident) {
  // Compose the "Likely RCA" line from the structured backend output.
  // Backend schema (RootCauseAnalysis) — failure_mechanism is the
  // canonical field. We compose:
  //   <failure_mechanism>  +  optional " Validation required through …"
  const rca = incident?.root_cause_analysis;
  if (rca?.failure_mechanism && typeof rca.failure_mechanism === "string") {
    const mech = rca.failure_mechanism.trim();
    const va = (rca.validation_assumptions || [])
      .map((s) => (typeof s === "string" ? s.trim() : ""))
      .filter(Boolean)
      .slice(0, 3);
    // If the mechanism already mentions validation, don't double-up.
    if (/validation required|requires validation|validate/i.test(mech)) {
      return mech;
    }
    if (va.length) {
      const tail = va.join("; ").toLowerCase();
      return `${mech} Validation required through ${tail}.`;
    }
    return mech;
  }

  // Fallback — pick the strongest NON-BUSINESS hypothesis that doesn't
  // read as a customer/revenue/SLA impact statement.
  const hyps = (incident?.hypotheses || []).filter((h) => {
    if (!h || h.domain === "business") return false;
    return !_impactOnly(h.hypothesis || "");
  });
  if (hyps.length) {
    const top = [...hyps].sort(
      (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0),
    )[0];
    if (top?.hypothesis) return top.hypothesis;
  }
  return "Likely RCA pending synthesis";
}

// Phrases that, when dominant in a hypothesis, indicate the agent wrote
// an impact statement instead of a technical/process cause.
const _IMPACT_ONLY_PATTERNS = [
  /customer abandonment/i,
  /revenue loss/i,
  /revenue exposure/i,
  /sla breach/i,
  /brand damage/i,
  /support contact/i,
  /customer frustration/i,
  /business disruption/i,
  /order rejection impact/i,
  /customer communication/i,
  /financial impact/i,
  /manual order processing/i,
  /stakeholder escalation/i,
  /payment abandonment/i,
  /reputational/i,
];

const _TECHNICAL_ANCHOR_RX =
  /\b(deploy|release|config|feature flag|timeout|retry|thread|memory|connection pool|replica|lock|io |cpu|pod|node|autoscal|dns|load balancer|tls|certificate|service mesh|routing|waf|rate limit|token|auth|database|migration|schema|race|idempot|queue|consumer|kafka|reconciliation|callback|webhook|api|dependency|integration|contract|ingress|egress|rollout|rollback|code|exception|regression|backlog|lag|latency|saturation|pressure|throttl|circuit|downstream|upstream|worker)/i;

function _impactOnly(text) {
  if (!text) return true;
  const hasImpact = _IMPACT_ONLY_PATTERNS.some((p) => p.test(text));
  if (!hasImpact) return false;
  // Has impact wording — accept only if there's a real technical anchor.
  return !_TECHNICAL_ANCHOR_RX.test(text);
}

function _shortenAction(action) {
  if (!action) return "";
  const trimmed = action.replace(/\s+/g, " ").trim();
  if (trimmed.length <= 160) return trimmed;
  // Cut at the first natural break (period, semicolon, " — ") within
  // the first 200 chars; otherwise hard-wrap at 160 with an ellipsis.
  const slice = trimmed.slice(0, 200);
  const breakIdx = Math.max(
    slice.search(/\.\s/),
    slice.lastIndexOf("; "),
    slice.lastIndexOf(" — "),
  );
  if (breakIdx > 80) {
    return trimmed.slice(0, breakIdx).trim() + ".";
  }
  return trimmed.slice(0, 157).trim() + "…";
}

function deriveNextAction(incident) {
  const top = incident?.consensus?.top_action;
  if (top?.action) return _shortenAction(top.action);
  const fap = incident?.final_action_plan;
  if (fap?.summary) return _shortenAction(fap.summary);
  return "Awaiting top action consensus";
}

// Compose the share-card Incident headline as "SEVERITY — title".
// Severity is normalised to upper-case (P1/P2/P3/P4); if absent we just
// return the title unchanged.
function deriveIncidentHeadline(incident) {
  const title = (incident?.title || "").trim();
  const sev = (incident?.severity || "").trim().toUpperCase();
  if (!sev) return title;
  if (!title) return sev;
  // Don't double-prefix if the title already starts with the severity.
  if (title.toUpperCase().startsWith(`${sev} `) || title.toUpperCase().startsWith(`${sev}—`) || title.toUpperCase().startsWith(`${sev} —`)) {
    return title;
  }
  return `${sev} — ${title}`;
}

function deriveApproval(incident) {
  const status = (incident?.status || "").toLowerCase();
  if (status === "approved") {
    return {
      text: "Approved by reviewer",
      pill: "Approved",
      state: "completed",
    };
  }
  if (status === "rejected") {
    return {
      text: "Action plan rejected — review required",
      pill: "Rejected",
      state: "blocked",
    };
  }
  return {
    text: "Required before remediation",
    pill: "Required",
    state: "required",
  };
}

/**
 * Build the brief view-model. Returns null if the incident has not yet
 * been analyzed enough to produce a sensible share card.
 */
export function buildShareBrief(incident) {
  if (!incident) return null;
  const hasAnalysis = Boolean(
    incident.consensus && (incident.consensus.top_action || (incident.consensus.ranked_actions || []).length),
  );
  if (!hasAnalysis) return null;

  const score = incident?.cre_score?.overall_score ?? null;
  const scoreLabel = incident?.cre_score?.overall_label || null;
  const ragColor = incident?.cre_score?.overall_rag || scoreRag(score);

  const confidence = deriveConfidence(incident);
  const approval = deriveApproval(incident);
  const fullLink = publicShareUrl(incident);

  return {
    incident_id: incident.id,
    title: deriveIncidentHeadline(incident),
    impact: deriveImpact(incident),
    likely_rca: deriveLikelyRCA(incident),
    score_value: score,
    score_label: scoreLabel,
    score_rag: ragColor,
    // Pill shows "High — 79%" so the brief carries both the label AND
    // the percentage the user expects to see.
    confidence_label: confidence.label_with_percent,
    confidence_dots: confidence.dots,
    confidence_rag: confidence.rag,
    next_action: deriveNextAction(incident),
    approval_text: approval.text,
    approval_pill: approval.pill,
    approval_state: approval.state,
    full_link_href: fullLink,
    link_display: publicShareDisplay(incident),
  };
}

/**
 * Plain-text share message — used by copy-to-clipboard and the Slack /
 * WhatsApp / Email / Teams deep-link share actions.
 */
export function briefToShareText(brief) {
  if (!brief) return "";
  const scoreLine =
    brief.score_value != null
      ? `CRE Score: ${Number(brief.score_value).toFixed(1)}/10${brief.score_label ? ` (${brief.score_label})` : ""}`
      : "CRE Score: pending — re-run analysis";
  const lines = [
    "CREAIOps — Incident Brief",
    "",
    `Incident: ${brief.title}`,
    `Impact: ${brief.impact}`,
    `Likely RCA: ${brief.likely_rca}`,
    scoreLine,
    `Confidence: ${brief.confidence_label}`,
    `Next Action: ${brief.next_action}`,
    `Approval: ${brief.approval_text} (${brief.approval_pill})`,
    "",
    `View Full Incident Analysis: ${brief.full_link_href || ""}`,
  ];
  return lines.join("\n");
}

export const _testHelpers = { scoreRag, deriveConfidence };
